import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ChefHat, Save, Coffee, Sun, Cookie, Moon, AlertTriangle, UtensilsCrossed, Plus, X, Printer, Copy, Pencil } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { MeseGiorniEsplosi } from "../components/MeseGiorniEsplosi";
import { toast } from "sonner";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
export const DIETA_PRESET = ["Diabetica", "Iposodica / senza sale", "Frullata / morbida (disfagia)", "Senza glutine", "Vegetariana", "Ipocalorica"];
const MEALS = [
  { key: "colazione", label: "Colazione", icon: Coffee, color: "text-rose-700", courses: false, ph: "Es. Latte, caffè, fette biscottate…" },
  { key: "pranzo", label: "Pranzo", icon: Sun, color: "text-amber-700", courses: true, ph: "Aggiungi una voce…" },
  { key: "merenda", label: "Merenda", icon: Cookie, color: "text-emerald-700", courses: false, ph: "Es. Tè, yogurt, frutta…" },
  { key: "cena", label: "Cena", icon: Moon, color: "text-indigo-700", courses: true, ph: "Aggiungi una voce…" },
];
const toLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const emptyDraft = () => ({ colazione: [], pranzo: [], merenda: [], cena: [] });

function PersonaMenuEditor({ o, structId, openDay, modelli, onSaved }) {
  const base = () => ({ colazione: "", pranzo: "", merenda: "", cena: "", ...(o.menu_giorno || {}) });
  const [d, setD] = React.useState(base());
  React.useEffect(() => { setD(base()); /* eslint-disable-next-line */ }, [o.menu_giorno, openDay, o.ospite_id]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const mp = o.menu_personalizzato || {};
  const hasAbituale = MEALS.some((m) => (mp[m.key] || "").trim());
  const dirty = MEALS.some((m) => (d[m.key] || "").trim() !== ((o.menu_giorno || {})[m.key] || "").trim());
  const save = async () => {
    try { await api.post("/menu/ospite/register", { structure_id: structId, ospite_id: o.ospite_id, data: openDay, ...d }); onSaved(); toast.success(`Menù salvato — ${o.cognome} ${o.nome}`); }
    catch { toast.error("Errore nel salvataggio"); }
  };
  const applyModel = (id) => { const md = modelli.find((x) => x.id === id); if (!md) return; setD({ colazione: md.colazione || "", pranzo: md.pranzo || "", merenda: md.merenda || "", cena: md.cena || "" }); toast.success(`Menù "${md.nome}" applicato — ricorda di salvare`); };
  const useAbituale = () => { setD({ colazione: mp.colazione || "", pranzo: mp.pranzo || "", merenda: mp.merenda || "", cena: mp.cena || "" }); toast.success("Menù abituale caricato — ricorda di salvare"); };
  return (
    <div className="rounded-lg border p-3 bg-amber-50/40" data-testid={`menu-dieta-${o.ospite_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="font-semibold text-sm">{o.cognome} {o.nome}{o.camera ? <span className="text-xs text-muted-foreground font-normal"> · Cam {o.camera}</span> : ""}</span>
        {(o.dieta || []).map((x) => <span key={x} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{x}</span>)}
        {(o.allergie || []).map((a) => <span key={a} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">no {a}</span>)}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {modelli.length > 0 && (
            <select defaultValue="" onChange={(e) => { if (e.target.value) { applyModel(e.target.value); e.target.value = ""; } }} className="h-8 border rounded-md px-1.5 text-xs" data-testid={`menu-dieta-modello-${o.ospite_id}`}>
              <option value="">Menù salvato…</option>
              {modelli.map((md) => <option key={md.id} value={md.id}>{md.nome}</option>)}
            </select>
          )}
          {hasAbituale && <button onClick={useAbituale} className="text-[11px] font-bold text-emerald-700 hover:underline" data-testid={`menu-dieta-abituale-${o.ospite_id}`}>Usa abituale</button>}
          <Button size="sm" variant={dirty ? "default" : "outline"} className={dirty ? "h-8" : "h-8 text-muted-foreground border-muted"} onClick={save} data-testid={`menu-dieta-save-${o.ospite_id}`}><Save size={13} className="mr-1" /> {dirty ? "Salva" : "Salvato"}</Button>
        </div>
      </div>
      {o.dieta_note && <div className="text-xs text-muted-foreground mb-2">{o.dieta_note}</div>}
      <div className="grid sm:grid-cols-2 gap-2">
        {MEALS.map((m) => (
          <div key={m.key}>
            <div className={`flex items-center gap-1 text-xs font-semibold ${m.color} mb-0.5`}><m.icon size={13} /> {m.label}</div>
            <Textarea rows={2} value={d[m.key] || ""} onChange={(e) => set(m.key, e.target.value)} placeholder={(mp[m.key] || "").trim() ? `Abituale: ${(mp[m.key] || "").trim()}` : "Una voce per riga…"} className="text-xs" data-testid={`menu-dieta-meal-${o.ospite_id}-${m.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Menu() {
  const { selectedStructure, structures } = useAuth();
  const qc = useQueryClient();
  const today = ymd(new Date());
  const [mese, setMese] = useState(today.slice(0, 7));
  const [openDay, setOpenDay] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [addVar, setAddVar] = useState({ ospite_id: "", pasto: "pranzo", piatto: "" });
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyRange, setCopyRange] = useState({ from: "", to: "" });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("today") === "1") {
      setMese(today.slice(0, 7));
      setOpenDay(today);
      searchParams.delete("today");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const structId = selectedStructure !== "all" ? selectedStructure : ((structures || []).length === 1 ? structures[0].id : null);

  const { data: monthData } = useQuery({
    queryKey: ["menu-month", structId, mese], enabled: !!structId, staleTime: 0, refetchOnMount: "always",
    queryFn: async () => (await api.get("/menu/month", { params: { structure_id: structId, mese } })).data,
  });
  const { data: dayData } = useQuery({
    queryKey: ["menu-day", structId, openDay], enabled: !!structId && !!openDay, staleTime: 0, refetchOnMount: "always",
    queryFn: async () => (await api.get("/menu/day", { params: { structure_id: structId, data: openDay } })).data,
  });
  const { data: ricorrenti = [] } = useQuery({
    queryKey: ["menu-ric", structId], enabled: !!structId,
    queryFn: async () => (await api.get("/menu/ricorrenti", { params: { structure_id: structId } })).data,
  });
  const { data: modelli = [] } = useQuery({
    queryKey: ["menu-modelli", structId], enabled: !!structId,
    queryFn: async () => (await api.get("/menu/modelli", { params: { structure_id: structId } })).data,
  });
  const { data: piattiOspiti = [] } = useQuery({
    queryKey: ["pasti-ospite-all", structId], enabled: !!structId, staleTime: 0,
    queryFn: async () => (await api.get("/menu/pasti-ospite", { params: { structure_id: structId } })).data,
  });

  useEffect(() => {
    const mm = dayData?.menu || {};
    setDraft({ colazione: toLines(mm.colazione), pranzo: toLines(mm.pranzo), merenda: toLines(mm.merenda), cena: toLines(mm.cena) });
  }, [dayData]);

  const setLine = (meal, i, v) => setDraft((p) => ({ ...p, [meal]: p[meal].map((l, idx) => (idx === i ? v : l)) }));
  const addLine = (meal, v = "") => setDraft((p) => ({ ...p, [meal]: [...p[meal], v] }));
  const delLine = (meal, i) => setDraft((p) => ({ ...p, [meal]: p[meal].filter((_, idx) => idx !== i) }));

  const serverLines = (k) => toLines(dayData?.menu?.[k]);
  const dirty = MEALS.some((m) => draft[m.key].map((s) => s.trim()).filter(Boolean).join("\n") !== serverLines(m.key).join("\n"));
  const saveDay = async () => {
    if (!structId) { toast.error("Seleziona una struttura specifica in alto."); return; }
    const body = { structure_id: structId, data: openDay };
    MEALS.forEach((m) => { body[m.key] = draft[m.key].map((s) => s.trim()).filter(Boolean).join("\n"); });
    try {
      await api.post("/menu/register", body);
      qc.invalidateQueries({ queryKey: ["menu-day"] }); qc.invalidateQueries({ queryKey: ["menu-month"] }); qc.invalidateQueries({ queryKey: ["menu-dash"] });
      toast.success("Menù del giorno salvato");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  const applyModel = (id) => {
    const md = modelli.find((x) => x.id === id); if (!md) return;
    setDraft({ colazione: toLines(md.colazione), pranzo: toLines(md.pranzo), merenda: toLines(md.merenda), cena: toLines(md.cena) });
    toast.success(`Menù "${md.nome}" applicato — ricorda di salvare`);
  };

  const openCopy = () => { setCopyRange({ from: openDay, to: openDay }); setCopyOpen(true); };
  const doCopy = async () => {
    const { from, to } = copyRange;
    if (!from || !to || to < from) { toast.error("Intervallo non valido"); return; }
    const dates = []; let d = new Date(from + "T00:00:00"); const end = new Date(to + "T00:00:00");
    while (d <= end) { dates.push(ymd(d)); d.setDate(d.getDate() + 1); }
    try {
      const r = await api.post("/menu/copy", { structure_id: structId, from_data: openDay, to_dates: dates });
      qc.invalidateQueries({ queryKey: ["menu-month"] }); qc.invalidateQueries({ queryKey: ["menu-day"] }); qc.invalidateQueries({ queryKey: ["menu-dash"] });
      setCopyOpen(false); toast.success(`Menù copiato in ${r.data.copiati} giorni`);
    } catch { toast.error("Errore nella copia"); }
  };
  const stampaPdf = async () => {
    try {
      const res = await api.get("/menu/pdf", { params: { structure_id: structId, mese }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Errore nella generazione del PDF"); }
  };

  const diete = dayData?.diete || [];
  const varianti = dayData?.varianti || [];
  const saveVariante = async (ospite_id, ospite_nome, pasto, piatto) => {
    try { await api.post("/menu/variante", { structure_id: structId, data: openDay, ospite_id, ospite_nome, pasto, piatto }); qc.invalidateQueries({ queryKey: ["menu-day"] }); qc.invalidateQueries({ queryKey: ["menu-month"] }); }
    catch { toast.error("Errore variante"); }
  };
  const addVariante = async () => {
    if (!addVar.ospite_id || !addVar.piatto.trim()) { toast.error("Scegli ospite e scrivi il piatto"); return; }
    const o = diete.find((x) => x.ospite_id === addVar.ospite_id) || {};
    await saveVariante(addVar.ospite_id, `${o.cognome || ""} ${o.nome || ""}`.trim(), addVar.pasto, addVar.piatto.trim());
    setAddVar({ ospite_id: "", pasto: "pranzo", piatto: "" }); toast.success("Variante aggiunta");
  };
  const convertAbituale = async (o) => {
    const mp = o.menu_personalizzato || {};
    const existing = new Set(varianti.filter((v) => v.ospite_id === o.ospite_id).map((v) => v.pasto));
    const targets = MEALS.filter((m) => (mp[m.key] || "").trim() && !existing.has(m.key));
    if (targets.length === 0) { toast.info("Già impostato come variante di oggi"); return; }
    for (const m of targets) {
      await saveVariante(o.ospite_id, `${o.cognome} ${o.nome}`.trim(), m.key, (mp[m.key] || "").trim());
    }
    toast.success("Menù abituale reso variante di oggi — ora puoi modificarlo");
  };
  const specialiOggi = diete.filter((o) => {
    const hasMp = o.menu_personalizzato && MEALS.some((m) => (o.menu_personalizzato[m.key] || "").trim());
    const hasGiorno = o.menu_giorno && MEALS.some((m) => (o.menu_giorno[m.key] || "").trim());
    return hasMp || hasGiorno;
  }).length;

  if (!structId) {
    return (
      <div>
        <h1 className="text-2xl font-head font-bold flex items-center gap-2 mb-3"><ChefHat size={22} className="text-orange-600" /> Cucina</h1>
        <div className="gr-card p-8 text-center" data-testid="menu-no-struct">
          <ChefHat size={34} className="mx-auto text-orange-400 mb-2" />
          <p className="text-sm text-muted-foreground">Seleziona una <b>struttura</b> dal selettore in alto per gestire il menù.</p>
        </div>
      </div>
    );
  }

  const dayContent = (
    <div className="space-y-4" data-testid="menu-day-content">
      {specialiOggi > 0 && (
        <div className="gr-card p-3 flex items-center gap-2 border-l-4 border-amber-400" data-testid="menu-speciali-count">
          <UtensilsCrossed size={18} className="text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">{specialiOggi} {specialiOggi === 1 ? "ospite ha" : "ospiti hanno"} un pasto personalizzato oggi</span>
          <span className="text-xs text-muted-foreground">— prepara le porzioni giuste</span>
        </div>
      )}
      {/* Menù del giorno */}
      <div className="gr-card p-5 border-2 border-orange-300 bg-orange-50/40 shadow-sm ring-1 ring-orange-100" data-testid="menu-giorno-card">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-head font-bold text-lg flex items-center gap-2 text-orange-700"><UtensilsCrossed size={20} className="text-orange-600" /> Menù del giorno</h3>
          <div className="flex flex-wrap items-center gap-2">
            {modelli.length > 0 && (
              <select defaultValue="" onChange={(e) => { if (e.target.value) { applyModel(e.target.value); e.target.value = ""; } }} className="h-9 border rounded-md px-2 text-sm" data-testid="menu-modello-select" title="Applica un menù salvato (creali in Menu & Pasti)">
                <option value="">Applica menù salvato…</option>
                {modelli.map((md) => <option key={md.id} value={md.id}>{md.nome}</option>)}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={openCopy} data-testid="menu-copy-open"><Copy size={14} className="mr-1" /> Copia in…</Button>
            <Button size="sm" variant={dirty ? "default" : "outline"} className={dirty ? "" : "text-muted-foreground border-muted"} onClick={saveDay} data-testid="menu-save-day"><Save size={14} className="mr-1" /> {dirty ? "Salva" : "Salvato"}</Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {MEALS.map((m) => (
            <div key={m.key} data-testid={`menu-meal-${m.key}`}>
              <div className={`flex items-center gap-1.5 text-sm font-semibold ${m.color} mb-1.5`}><m.icon size={15} /> {m.label}</div>
              <ul className="space-y-1.5">
                {draft[m.key].map((line, i) => (
                  <li key={i} className="flex items-center gap-1.5" data-testid={`menu-line-${m.key}-${i}`}>
                    <span className="text-orange-400 font-bold">•</span>
                    <Input list="menu-ric-datalist" value={line} onChange={(e) => setLine(m.key, i, e.target.value)} placeholder={m.ph} className="h-9 text-sm flex-1" data-testid={`menu-input-${m.key}-${i}`} />
                    <button onClick={() => delLine(m.key, i)} className="text-muted-foreground hover:text-rose-600 shrink-0" data-testid={`menu-line-del-${m.key}-${i}`}><X size={15} /></button>
                  </li>
                ))}
                {draft[m.key].length === 0 && <li className="text-xs text-muted-foreground italic pl-4">Nessuna voce.</li>}
              </ul>
              <button onClick={() => addLine(m.key)} className="mt-1.5 text-xs font-semibold text-orange-700 hover:underline inline-flex items-center gap-1" data-testid={`menu-addline-${m.key}`}><Plus size={13} /> Aggiungi voce</button>
            </div>
          ))}
        </div>
        <datalist id="menu-ric-datalist">{ricorrenti.map((r) => <option key={r.id} value={r.testo} />)}</datalist>
      </div>

      {/* Diete e attenzioni — menù personale del giorno */}
      <div className="gr-card p-4" data-testid="menu-diete">
        <h3 className="font-head font-bold flex items-center gap-2 mb-1 text-amber-700"><AlertTriangle size={18} /> Diete e attenzioni</h3>
        <p className="text-xs text-muted-foreground mb-3">Menù personale di ogni ospite con allergie, dieta o attenzioni per questo giorno. Puoi compilarlo, modificarlo o aggiungerlo qui: si salva solo per oggi.</p>
        {diete.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="menu-diete-empty">Nessun ospite con allergie, dieta o pasto speciale.</p>
        ) : (
          <div className="space-y-3">
            {diete.map((o) => (
              <PersonaMenuEditor key={o.ospite_id} o={o} structId={structId} openDay={openDay} modelli={modelli}
                onSaved={() => { qc.invalidateQueries({ queryKey: ["menu-day"] }); qc.invalidateQueries({ queryKey: ["menu-month"] }); }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><ChefHat size={22} className="text-orange-600" /> Cucina</h1>
          <p className="text-sm text-muted-foreground">Puoi pianificare anche i giorni futuri e il mese successivo. Le diete degli ospiti sono evidenziate in automatico.</p>
        </div>
        <Button size="sm" variant="outline" onClick={stampaPdf} data-testid="menu-pdf-mese"><Printer size={14} className="mr-1" /> Stampa mese (PDF)</Button>
      </div>

      <MeseGiorniEsplosi embedded color="#ea7317" allowFuture futureMonths={1} largeCells
        renderDayExtra={(iso) => {
          const d = monthData?.days?.[iso];
          const meals = d?.meals || {};
          const speciali = d?.speciali || 0;
          const hasAny = MEALS.some((m) => (meals[m.key] || "").trim());
          return (
            <div className="mt-2" data-testid={`menu-day-preview-${iso}`}>
              {hasAny ? (
                <div className="space-y-0.5">
                  {MEALS.map((m) => {
                    const txt = (meals[m.key] || "").replace(/\n/g, " · ").trim();
                    if (!txt) return null;
                    return <div key={m.key} className="text-[10px] leading-tight text-slate-600 truncate"><span className={`font-bold ${m.color}`}>{m.label[0]}:</span> {txt}</div>;
                  })}
                </div>
              ) : <div className="text-[10px] text-muted-foreground italic">Menù da inserire</div>}
              {speciali > 0 && <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`menu-day-speciali-${iso}`}><UtensilsCrossed size={11} /> {speciali} a parte</div>}
            </div>
          );
        }}
        mese={mese} onMese={(m) => { setMese(m); setOpenDay(null); }} openDay={openDay} onOpenDay={setOpenDay}
        counts={monthData?.counts || {}} countLabel="pasti">
        {dayContent}
      </MeseGiorniEsplosi>

      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent data-testid="menu-copy-dialog">
          <DialogHeader><DialogTitle>Copia menù del giorno</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Copia il menù di <b>{openDay}</b> nei giorni indicati (i giorni esistenti verranno sovrascritti).</p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => { const d = new Date(openDay + "T00:00:00"); d.setDate(d.getDate() + 1); const s = ymd(d); d.setDate(d.getDate() + 6); setCopyRange({ from: s, to: ymd(d) }); }} data-testid="menu-copy-week">Prossimi 7 giorni</Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold mb-1 block">Dal</label><Input type="date" value={copyRange.from} onChange={(e) => setCopyRange((p) => ({ ...p, from: e.target.value }))} data-testid="menu-copy-from" /></div>
              <div><label className="text-xs font-semibold mb-1 block">Al</label><Input type="date" value={copyRange.to} onChange={(e) => setCopyRange((p) => ({ ...p, to: e.target.value }))} data-testid="menu-copy-to" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Annulla</Button>
            <Button onClick={doCopy} data-testid="menu-copy-confirm"><Copy size={14} className="mr-1" /> Copia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
